import React, { useState, useContext } from 'react';
import { UserContext } from '../UserAndProfile/UserContext';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  CircularProgress 
} from '@mui/material';
import { Send } from '@mui/icons-material';

const StudentInviteForm: React.FC = () => {
  const { sendReferralInvitation } = useContext(UserContext);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useContext(UserContext);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName.trim() || !email.trim()) {
      setError('Please fill in all fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await sendReferralInvitation({
        firstName: firstName.trim(),
        email: email.trim().toLowerCase(),
        role: user?.userRole as 'parent' | 'student' | 'educator',
      });
      
      setSubmitted(true);
      setFirstName('');
      setEmail('');
    } catch (err) {
      setError('Failed to send invitation. Please try again.');
      console.error('Error sending student invitation:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Invitation sent successfully! Your student will receive an email with signup instructions.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Student's First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          fullWidth
          size="small"
          disabled={isSubmitting}
          required
        />

        <TextField
          label="Student's Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          size="small"
          disabled={isSubmitting}
          required
          helperText="This should be the email your student will use to sign up"
        />

        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <Send />}
          sx={{ mt: 1 }}
        >
          {isSubmitting ? 'Sending...' : 'Send Invitation'}
        </Button>
      </Box>
    </Box>
  );
};

export default StudentInviteForm; 